import { debounce } from 'lodash';
import { useLayoutEffect, useRef, useState } from 'react';
import { Color } from '../../../../../modules/products/product.types.v2';
import { getVisibleColorsCount, orderColorsWithSelectedFirst } from './productCardV2Colors.utils';

interface UseProductCardV2ColorsParams {
  allColors: Omit<Color, 'code'>[];
  selectedColor?: Omit<Color, 'code'>;
}

const RESIZE_DEBOUNCE = 100;

const useProductCardV2Colors = ({ allColors, selectedColor }: UseProductCardV2ColorsParams) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const ordered = orderColorsWithSelectedFirst({ allColors, selectedColor });
  const totalColors = ordered.length;
  const [visibleCount, setVisibleCount] = useState(totalColors);

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    const recalculate = () => {
      const containerWidth = container.getBoundingClientRect().width;
      if (containerWidth <= 0) {
        return;
      }

      setVisibleCount(getVisibleColorsCount({ containerWidth, totalColors }));
    };

    recalculate();

    const recalculateDebounced = debounce(recalculate, RESIZE_DEBOUNCE);
    const observer = new ResizeObserver(recalculateDebounced);
    observer.observe(container);

    return () => {
      observer.disconnect();
      recalculateDebounced.cancel();
    };
  }, [totalColors]);

  const visible = ordered.slice(0, visibleCount);
  const overflow = ordered.length - visible.length;

  return { containerRef, visible, overflow };
};

export default useProductCardV2Colors;
